import { Component, Inject, OnDestroy, Optional } from '@angular/core';
import { TIER1_SIGNUP_LEGAL_EXTRA_LINKS, TIER1_SIGNUP_LEGAL_TEXT_PARAMS } from '~shared/constants/legal-text.constants';
import { RegistrationTypeLabels } from '~shared/modules/application-event/constants';
import { ActivatedRoute, Router, UrlSerializer } from '@angular/router';
import { Subject } from 'rxjs';
import { switchMap, take, takeUntil } from 'rxjs/operators';
import { CheckEmailService } from '../../services/check.email.service';
import {
  APP_USER_PASSWORDLESS_ADD_BUSINESS_DETAILS_ROUTE,
  APP_USER_TAX_ID_CHECK_ROUTE,
  APP_USER_VALIDATE_EMAIL_ROUTE,
  APP_USER_VAT_ID_CHECK_ROUTE,
  CountryCode,
  NUMBER_1,
} from '~shared/constants';
import isEmpty from 'lodash.isempty';

import { PasswordlessService } from '../../services/passwordless.service';
import { CountryBasedTierTwoConfigService } from '../../services/country-based-tier-two-config.service';
import { REQUEST } from '@nguniversal/express-engine/tokens';
import { getCountryDomainCode } from '~server/utils/country-detection.util';

@Component({
  selector: 'app-passwordless-signup',
  templateUrl: './passwordless-signup.component.html',
  styleUrls: ['./passwordless-signup.component.scss'],
})
export class PasswordlessSignupComponent implements OnDestroy {
  public tier1SignupLegalTextParams = TIER1_SIGNUP_LEGAL_TEXT_PARAMS();
  public tier1SignupLegalExtraLinks = TIER1_SIGNUP_LEGAL_EXTRA_LINKS();
  public showSpinner = false;
  private unsubscribe$ = new Subject<any>();
  private secureLoggedEmail: string;
  private isPasswordless = true;
  private backUrl: string;

  // prettier-ignore
  constructor(  // NOSONAR
    private router: Router,
    private route: ActivatedRoute,
    private serializer: UrlSerializer,
    private checkEmailService: CheckEmailService,
    private passwordlessService: PasswordlessService,
    private countryBasedTierTwoConfigService: CountryBasedTierTwoConfigService,
    @Optional() @Inject(REQUEST) private webAppBuyerRequest?: any) {
    const { validatedEmail, backUrl = '/' } = this.route.snapshot.queryParams;
    this.backUrl = backUrl;

    if (validatedEmail) {
      this.isEmailExist(validatedEmail);
    } else {
      this.goBacktoValidateEmailPage();
    }
  }

  public ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  isEmailExist(validatedEmail: boolean): void {
    this.checkEmailService.currentEmail$.pipe(takeUntil(this.unsubscribe$)).subscribe((userEmail) => {
      if (!validatedEmail) {
        userEmail = '';
      }
      if (isEmpty(userEmail)) {
        this.goBacktoValidateEmailPage();
      } else {
        this.secureLoggedEmail = userEmail;
      }
    });
  }

  goBacktoValidateEmailPage(): void {
    const urlTree = this.router.createUrlTree([APP_USER_VALIDATE_EMAIL_ROUTE], {
      queryParams: { backUrl: this.backUrl },
    });
    this.router.navigateByUrl(this.serializer.serialize(urlTree));
  }

  personalSignup(event: MouseEvent) {
    event.preventDefault();
    this.showSpinner = true;
    this.passwordlessService.fireGAEventBasedOnRegistrationType(RegistrationTypeLabels.Personal);
    this.passwordlessService
      .tierOneSignupForPasswordLessFlow(
        this.secureLoggedEmail,
        this.isPasswordless,
        false,
        false,
        false,
        this.resetSpinner
      )
      .subscribe();
  }

  businessSignup(event: MouseEvent) {
    event.preventDefault();
    this.showSpinner = true;
    this.passwordlessService.fireGAEventBasedOnRegistrationType(RegistrationTypeLabels.Business);

    this.countryBasedTierTwoConfigService.getCompanyDetailsFromVATId$
      .pipe(take(NUMBER_1))
      .pipe(
        switchMap((isEnabled) => {
          const market = getCountryDomainCode(this.webAppBuyerRequest);
          const isDutchMarket = market === CountryCode.Netherlands;
          const isFrenchMarket = market === CountryCode.France;
          const isTaxIdCountry = isDutchMarket || isFrenchMarket;

          if (!isEnabled && !isTaxIdCountry) {
            return this.router.navigate([`/${APP_USER_PASSWORDLESS_ADD_BUSINESS_DETAILS_ROUTE}`], {
              queryParams: {
                backUrl: this.backUrl,
                secureLoggedEmail: this.secureLoggedEmail,
                validatedEmail: 'true',
              },
              state: { passwordlessTier2: 'true' },
            });
          }

          const url = isEnabled ? APP_USER_VAT_ID_CHECK_ROUTE : APP_USER_PASSWORDLESS_ADD_BUSINESS_DETAILS_ROUTE;
          return this.passwordlessService.tierOneSignupForPasswordLessFlow(
            this.secureLoggedEmail,
            this.isPasswordless,
            true,
            false,
            true,
            this.resetSpinner,
            isTaxIdCountry ? APP_USER_TAX_ID_CHECK_ROUTE : url
          );
        })
      )
      .subscribe();
  }

  businessMetroSignup(event: MouseEvent) {
    event.preventDefault();
    this.showSpinner = true;
    this.passwordlessService.fireGAEventBasedOnRegistrationType(RegistrationTypeLabels.Metro);
    this.passwordlessService
      .tierOneSignupForPasswordLessFlow(
        this.secureLoggedEmail,
        this.isPasswordless,
        true,
        true,
        false,
        this.resetSpinner
      )
      .subscribe();
  }

  private resetSpinner = () => {
    this.showSpinner = false;
  };
}
